import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Col,
  Form,
  Input,
  Radio,
  Result,
  Row,
  Space,
  Steps,
  Tooltip,
  Typography,
} from "antd";
import { connect, useDispatch, useSelector } from "react-redux";
import { GroupFormItem, RoleFormItem, selectHasRole } from "../UtilsAdmin";
import { applicationPortal, service_admin_role } from "../../../config";
import {
  getApplicationListV2,
  getServiceApplicationSubscriptionSettings,
  getServiceCollections,
  linkServiceApplicationToServiceCollection,
  postCreateManagedResourceType,
  putServiceApplicationSubscriptionSettings,
} from "../../../actions/api";
import {
  AppstoreAddOutlined,
  EditOutlined,
  LinkOutlined,
  ReloadOutlined,
} from "@ant-design/icons/lib";
import ApplicationSearch from "../../common/ApplicationSearch";
import { HelpApplicationSubscription } from "../help/HelpApplicationSubscription";
import { HelpManagedResourceType } from "../help/HelpManagedResourceType";
import { HelpGeneralConcept } from "../help/HelpGeneralConcept";

const { Text, Title } = Typography;

const WizardAdmin = () => {
  const dispatch = useDispatch();
  const [serviceCollections, setServiceCollections] = useState([]);
  const isServiceAdmin = useSelector(selectHasRole(service_admin_role));

  const [selectedServiceCollectionId, setSelectedServiceCollectionId] =
    useState();
  const [selectedServiceApplication, setSelectedServiceApplication] =
    useState();
  const [selectedApplicationSettings, setSelectedApplicationSettings] =
    useState();
  const [applicationSubscriptionId, setApplicationSubscriptionId] = useState();
  const [current, setCurrent] = useState(0);

  const [form] = Form.useForm();
  const layout = {
    labelCol: { span: 8 },
    wrapperCol: { span: 16 },
  };

  const fetchCollections = useCallback(async () => {
    setServiceCollections([]);

    const collections = await dispatch(getServiceCollections());
    const applications = await dispatch(getApplicationListV2());
    if (collections?.payload?.data) {
      if (isServiceAdmin) setServiceCollections(collections.payload.data);
      else if (applications?.payload?.data)
        setServiceCollections(
          collections.payload.data.filter((x) =>
            applications.payload.data.some(
              (x2) => x.applicationIdentifier === x2.applicationIdentifier,
            ),
          ),
        );
    }
  }, [dispatch, isServiceAdmin]);

  useEffect(() => {
    fetchCollections();
  }, [fetchCollections]);

  const fetchApplicationSubscriptionSettings = useCallback(
    async (selectedServiceApplication) => {
      if (selectedServiceApplication) {
        const action = await dispatch(
          getServiceApplicationSubscriptionSettings(
            selectedServiceApplication.applicationIdentifier,
          ),
        );
        if (action?.payload?.data) {
          setSelectedApplicationSettings(action.payload.data);
          setApplicationSubscriptionId(action.payload.data.id);
        }
      }
    },
    [dispatch],
  );

  const selectApplication = async (application) => {
    await dispatch(
      linkServiceApplicationToServiceCollection(
        selectedServiceCollectionId,
        application.applicationIdentifier,
      ),
    );
    setSelectedServiceApplication(application);
    await fetchApplicationSubscriptionSettings(application);
  };

  const onChange = (e) => {
    setSelectedServiceCollectionId(e.target.value);
  };

  const next = () => {
    setCurrent(current + 1);
  };
  const prev = () => {
    setCurrent(current - 1);
  };

  const onFinishApplicationSettings = async (values) => {
    await dispatch(
      putServiceApplicationSubscriptionSettings(
        selectedServiceApplication.applicationIdentifier,
        values,
      ),
    );
    setApplicationSubscriptionId(selectedApplicationSettings.id);
  };
  const onFinishFailedApplicationSettings = (errorInfo) => {
    console.log("Failed:", errorInfo);
  };

  const onFinishManagedResourceType = async (values) => {
    console.log("values", values);
    await dispatch(postCreateManagedResourceType(values));
  };
  const onFinishFailedManagedResourceType = (errorInfo) => {
    console.log("Failed:", errorInfo);
  };

  const wizardButtons = (condition, last) => {
    return (
      <Space style={{ marginTop: "20px" }}>
        <Button disabled={current === 0} onClick={() => prev()}>
          Previous
        </Button>
        {!last && (
          <Button type="primary" disabled={!condition} onClick={() => next()}>
            Next
          </Button>
        )}
        {/* {last && (
          <Button
            type="primary"
            disabled={!condition}
            onClick={() => message.success("Processing complete!")}
          >
            Done
          </Button>
        )} */}
      </Space>
    );
  };

  const steps = [
    {
      title: "Wizard goal",
      description: current === 0 && "",
      content: (
        <Space direction="vertical">
          <Title level={3} style={{ marginTop: "0px" }}>
            Adding a new Service Application to an existing Service Collection
          </Title>
          <HelpGeneralConcept />

          {wizardButtons(true, false)}
        </Space>
      ),
    },
    {
      title: "Select a Service Collection",
      description:
        current === 1 &&
        "The Service Collection is the top level Service where Eligibility applies. Your Application will then be listed under the selected entry.",
      content: (
        <Space direction="vertical">
          <Text>
            Service Collections are created by administrators only. If you need
            a new one or access to an existing that is not listed here, please
            open a SNOW ticket.
          </Text>
          <Radio.Group
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
            value={selectedServiceCollectionId}
            onChange={onChange}
            options={serviceCollections.map((sc) => ({
              value: sc.id,
              label: (
                <>
                  <Text strong>{sc.displayName}</Text>
                  <br />
                  <Text type="secondary">{sc.description}</Text>
                </>
              ),
            }))}
          />
          {wizardButtons(selectedServiceCollectionId !== undefined, false)}
        </Space>
      ),
    },
    {
      title: "Assign an Application",
      description:
        current === 2 &&
        "Linking an Application to this Service Collection will make it available below, and apply its eligibility settings.",
      content: (
        <Space direction="vertical">
          <Text>
            To add a new application to this Service Collection, first create an
            application if needed on the Application Portal then map it to this
            Service Collection using the buttons below.
          </Text>

          <Tooltip title="Create a new Application on the Application Portal">
            <Button
              icon={<AppstoreAddOutlined />}
              href={applicationPortal}
              target="_blank"
            >
              Create a new Application
            </Button>
          </Tooltip>
          <ApplicationSearch
            title="Map an Application to Service Collection"
            icon={<LinkOutlined />}
            onSelect={selectApplication}
            buttonText="Map an Application"
          />

          {selectedServiceApplication ? (
            <Result
              status="success"
              title="Successfully mapped Application"
              subTitle={
                <>
                  Application{" "}
                  {selectedServiceApplication?.applicationIdentifier} was
                  mapped.
                </>
              }
            />
          ) : (
            <></>
          )}

          {wizardButtons(selectedServiceApplication !== undefined, false)}
        </Space>
      ),
    },
    {
      title: "Configure Subscription Settings",
      description:
        current === 3 &&
        "Subscription Settings define how users can subscribed to the Application, automatically or manually.",
      content: (
        <Space direction="vertical">
          <Text>
            You might need to edit the Application roles via the Application
            Portal. When done, click Refresh to reload the new parameters, and
            use the form below to set the subscription values.
          </Text>
          <Space>
            <Tooltip title="Edit this Application in the Application Portal">
              <Button
                icon={<EditOutlined />}
                href={`${applicationPortal}manage/${selectedServiceApplication?.applicationIdentifier}`}
                target="_blank"
              >
                Edit this Application and its roles
              </Button>
            </Tooltip>
            <Tooltip title="When done editing on the Application Portal, a refresh is needed">
              <Button
                icon={<ReloadOutlined />}
                onClick={async () =>
                  await fetchApplicationSubscriptionSettings(
                    selectedServiceApplication?.applicationIdentifier,
                  )
                }
              >
                Refresh Subscription Settings
              </Button>
            </Tooltip>
          </Space>

          <Form
            {...layout}
            form={form}
            initialValues={selectedApplicationSettings}
            onFinish={onFinishApplicationSettings}
            onFinishFailed={onFinishFailedApplicationSettings}
            style={{ maxWidth: 600, marginTop: "10px" }}
          >
            <Form.Item label="Settings Name" name="name">
              <Input disabled />
            </Form.Item>
            <Form.Item label="ApplicationId" name="applicationId">
              <Input disabled />
            </Form.Item>

            <RoleFormItem
              form={form}
              label="Subscribed Role"
              name="subscribedRoleId"
              searchLabel="Select role used for Subscribed people"
              applicationId={selectedServiceApplication?.applicationIdentifier}
              required={true}
            />
            <GroupFormItem
              form={form}
              label="Self Subscription Group"
              name="selfSubscriptionGroupId"
              searchLabel="Select group used for Self Subscription (and mapped to Subscribed Role defined above)"
              applicationId={selectedServiceApplication?.applicationIdentifier}
              roleId={selectedApplicationSettings?.subscribedRoleId}
            />
            <RoleFormItem
              form={form}
              label="Denied Role"
              name="deniedRoleId"
              searchLabel="Select role used for Denied people"
              applicationId={selectedServiceApplication?.applicationIdentifier}
            />
            <RoleFormItem
              form={form}
              label="Admin Role"
              name="adminRoleId"
              searchLabel="Select role used for Admin people"
              applicationId={selectedServiceApplication?.applicationIdentifier}
            />

            <Form.Item label="Grace Period Days" name="gracePeriodDays">
              <Input placeholder="Number of days" />
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit">
                Save
              </Button>
            </Form.Item>
          </Form>

          {wizardButtons(applicationSubscriptionId !== undefined, false)}
          <HelpApplicationSubscription />
        </Space>
      ),
    },
    {
      title: "Create Managed Resource Type",
      description:
        current === 4 &&
        "Optional: Create a managed resource type if this application will support creating resources. If not needed just click Done.",
      content: (
        <Space direction="vertical">
          <Text></Text>
          <Form
            {...layout}
            form={form}
            initialValues={{
              initialState: "Active",
              applicationSubscriptionId: applicationSubscriptionId,
            }}
            onFinish={onFinishManagedResourceType}
            onFinishFailed={onFinishFailedManagedResourceType}
            style={{ maxWidth: 600, marginTop: "10px" }}
          >
            <Form.Item
              label="Managed Resource Type Name"
              name="resourceTypeName"
            >
              <Input placeholder="Managed Resource Type Name" />
            </Form.Item>

            <Form.Item label="Block Grace Period Days" name="blockGracePeriod">
              <Input placeholder="Number of days" />
            </Form.Item>
            <Form.Item
              label="Archive Grace Period Days"
              name="archiveGracePeriod"
            >
              <Input placeholder="Number of days" />
            </Form.Item>
            <Form.Item
              label="Delete Grace Period Days"
              name="deleteGracePeriod"
            >
              <Input placeholder="Number of days" />
            </Form.Item>
            <Form.Item
              label="Application Subscription Settings Id"
              name="applicationSubscriptionId"
              hidden
            >
              <Input disabled />
            </Form.Item>
            <Form.Item label="Initial State" name="initialState">
              <Radio.Group>
                <Radio.Button value="Active">Active</Radio.Button>
                <Radio.Button value="Disabled">Disabled</Radio.Button>
              </Radio.Group>
            </Form.Item>

            <Form.Item
              label="Manage Resource URL template"
              name="managementUrlTemplate"
            >
              <Input placeholder="Manage Resource URL" />
            </Form.Item>
            <Form.Item label="Create Resource URL" name="createResourceUrl">
              <Input placeholder="Create Resource URL" />
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit">
                Create
              </Button>
            </Form.Item>
          </Form>

          {wizardButtons(true, false)}

          <HelpManagedResourceType />
        </Space>
      ),
    },
    {
      title: "Completed",
      description:
        current === 5 &&
        "Application was successfully registered and is available on the portal.",
      content: (
        <Space direction="vertical">
          <Result
            status="success"
            title="Successfully registered Application"
            subTitle={
              <>
                Application {selectedServiceApplication?.applicationIdentifier}{" "}
                was registered.
              </>
            }
          />
          {wizardButtons(true, true)}
        </Space>
      ),
    },
  ];

  return (
    <>
      <Row>
        <Col span={6}>
          <Steps
            current={current}
            items={steps.map((item) => ({
              key: item.title,
              title: item.title,
              description: item.description,
            }))}
            direction="vertical"
          />
        </Col>
        <Col span={18}>{steps[current].content}</Col>
      </Row>
    </>
  );
};

export default connect()(WizardAdmin);
